/** Shared draw-on-image modal. Loads any image source (R2 /files URL or a
 * live viewport screenshot data-URL), lets the user mark it up, optionally
 * collects a note + a scope choice, and hands back the annotated blob. */

export function annotateImage({ src, note = false, scopeOptions = null, onSave, toast }) {
  const modal = document.createElement("div");
  modal.className = "modal";
  modal.innerHTML = `
    <div class="modal-box">
      <div class="modal-bar">
        <b>${note ? "Draw where you mean, then say what's wrong" : "Draw on reference"}</b>
        ${["#ff3b30", "#ffcc00", "#34c759", "#0a84ff", "#ffffff", "#000000"].map((c, i) => `<button class="swatch${i === 0 ? " on" : ""}" data-c="${c}" style="background:${c}"></button>`).join("")}
        <label class="slider">✏️<input type="range" class="brush" min="2" max="24" value="6" /></label>
        <span class="spacer"></span>
        <button class="m-undo">undo</button>
        <button class="m-cancel">cancel</button>
        <button class="m-save primary">${note ? "send feedback" : "save as new reference"}</button>
      </div>
      <canvas></canvas>
      ${note ? `<div class="modal-note">
        ${scopeOptions ? `<select class="m-scope">${scopeOptions.map((o) => `<option value="${o.value}">${o.label}</option>`).join("")}</select>` : ""}
        <textarea class="m-note" rows="2" placeholder="what should change here? (required)"></textarea>
      </div>` : ""}
    </div>`;
  document.body.appendChild(modal);
  const canvas = modal.querySelector("canvas");
  const ctx = canvas.getContext("2d");
  let color = "#ff3b30", brush = 6, drawing = false, snapshots = [];

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    const scale = Math.min(1, 1280 / img.width, 720 / img.height);
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  };
  img.onerror = () => { toast("could not load image for annotation", true); modal.remove(); };
  img.src = src;

  // track where strokes land (normalized 0..1) so callers can resolve the
  // drawn region back into 3D — the decal-placement anchor
  const stroke = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity, any: false };
  const trackStroke = (p) => {
    stroke.any = true;
    stroke.minX = Math.min(stroke.minX, p.x / canvas.width); stroke.maxX = Math.max(stroke.maxX, p.x / canvas.width);
    stroke.minY = Math.min(stroke.minY, p.y / canvas.height); stroke.maxY = Math.max(stroke.maxY, p.y / canvas.height);
  };
  const strokeBounds = () =>
    stroke.any
      ? { cx: (stroke.minX + stroke.maxX) / 2, cy: (stroke.minY + stroke.maxY) / 2, w: stroke.maxX - stroke.minX, h: stroke.maxY - stroke.minY }
      : null;

  const pos = (e) => {
    const r = canvas.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: ((p.clientX - r.left) / r.width) * canvas.width, y: ((p.clientY - r.top) / r.height) * canvas.height };
  };
  const start = (e) => { e.preventDefault(); snapshots.push(ctx.getImageData(0, 0, canvas.width, canvas.height)); if (snapshots.length > 20) snapshots.shift(); drawing = true; const p = pos(e); trackStroke(p); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const p = pos(e); trackStroke(p); ctx.lineTo(p.x, p.y); ctx.strokeStyle = color; ctx.lineWidth = brush; ctx.lineCap = ctx.lineJoin = "round"; ctx.stroke(); };
  const end = () => (drawing = false);
  canvas.addEventListener("mousedown", start); canvas.addEventListener("mousemove", move); addEventListener("mouseup", end);
  canvas.addEventListener("touchstart", start); canvas.addEventListener("touchmove", move); canvas.addEventListener("touchend", end);

  modal.querySelectorAll(".swatch").forEach((b) => (b.onclick = () => { color = b.dataset.c; modal.querySelectorAll(".swatch").forEach((x) => x.classList.toggle("on", x === b)); }));
  modal.querySelector(".brush").oninput = (e) => (brush = Number(e.target.value));
  modal.querySelector(".m-undo").onclick = () => { const s = snapshots.pop(); if (s) ctx.putImageData(s, 0, 0); };
  modal.querySelector(".m-cancel").onclick = () => modal.remove();
  modal.querySelector(".m-save").onclick = () => {
    const noteText = note ? modal.querySelector(".m-note").value.trim() : "";
    if (note && !noteText) { toast("write what should change — the note travels with the drawing", true); return; }
    const scopeValue = modal.querySelector(".m-scope")?.value;
    canvas.toBlob((blob) => onSave({ blob, note: noteText, scopeValue, strokeBounds: strokeBounds(), close: () => modal.remove() }), "image/png");
  };
}
